import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { getUser, blockUser, unblockUser } from '../services/userService';
import { coordsToFrequency } from '../lib/frequency';
import { colors } from '../styles/colors';
import { IconClose, IconCopy } from './icons';
import { DefaultAvatar } from './DefaultAvatar';
import { maskEmail } from '../utils/email';
import { useConfigStore } from '../stores/configStore';
import type { User } from '../types/user';

interface UserProfileModalProps {
  uid: string;
  nickname?: string;
  onClose: () => void;
  onSendDM?: (recipientId: string, recipientNickname: string) => void;
  onReport?: (targetId: string, targetNickname: string) => void;
}

export default function UserProfileModal({ uid, nickname, onClose, onSendDM, onReport }: UserProfileModalProps) {
  const { t, i18n } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const config = useConfigStore((s) => s.config);
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedFreq, setCopiedFreq] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);

  const isBlocked = me?.blockedUserIds?.includes(uid) ?? false;

  useEffect(() => {
    setLoading(true);
    getUser(uid)
      .then((u) => setProfile(u))
      .finally(() => setLoading(false));
  }, [uid]);

  const isKo = i18n.language === 'ko';

  const handleBlock = async () => {
    if (!me) return;
    if (isBlocked) {
      const msg = isKo
        ? `${profile?.nickname ?? nickname}님의 차단을 해제하시겠습니까?`
        : `Unblock ${profile?.nickname ?? nickname}?`;
      if (!confirm(msg)) return;
      await unblockUser(me.uid, uid);
      useAuthStore.getState().setUser({
        ...me,
        blockedUserIds: me.blockedUserIds.filter((id) => id !== uid),
      });
      onClose();
    } else {
      const msg = isKo
        ? `${profile?.nickname ?? nickname}님을 차단하시겠습니까?`
        : `Block ${profile?.nickname ?? nickname}?`;
      if (!confirm(msg)) return;
      await blockUser(me.uid, uid);
      useAuthStore.getState().setUser({
        ...me,
        blockedUserIds: [...(me.blockedUserIds ?? []), uid],
      });
    }
  };

  const handleCopyFreq = () => {
    if (!profile?.currentLocation) return;
    const freq = coordsToFrequency(profile.currentLocation.lat, profile.currentLocation.lng);
    navigator.clipboard.writeText(freq).then(() => {
      setCopiedFreq(true);
      setTimeout(() => setCopiedFreq(false), 1500);
    });
  };

  const genderLabel = profile?.gender === 'male' ? t('auth.male') : profile?.gender === 'female' ? t('auth.female') : '';
  const genderColor = profile?.gender === 'male' ? '#4a9eff' : profile?.gender === 'female' ? '#ff6b9d' : colors.textMuted;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { if (!showFullImage) onClose(); }}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative z-10 w-full max-w-sm overflow-hidden rounded-2xl"
        style={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: `1px solid ${colors.border}` }}>
          <h3 className="text-sm font-bold" style={{ color: colors.text }}>{t('profile.title')}</h3>
          <button onClick={onClose} className="rounded p-1 hover:bg-white/10">
            <IconClose size={18} color={colors.textMuted} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm" style={{ color: colors.textMuted }}>{t('common.loading')}</p>
          </div>
        ) : profile ? (
          <>
            {/* Profile Info */}
            <div className="flex flex-col items-center px-4 py-5">
              {/* Avatar - clickable for full view */}
              <div className="relative mb-3">
                <button
                  onClick={() => { if (profile.profileImageUrl) setShowFullImage(true); }}
                  className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full"
                  style={{
                    border: `3px solid ${genderColor}`,
                    backgroundColor: colors.card,
                    cursor: profile.profileImageUrl ? 'pointer' : 'default',
                  }}
                >
                  {profile.profileImageUrl ? (
                    <img src={profile.profileImageUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <DefaultAvatar gender={profile.gender} size={48} />
                  )}
                </button>
                {profile.isBot && config.showBotBadge !== false && (
                  <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold text-white"
                    style={{ backgroundColor: '#9333ea', border: `2px solid ${colors.surface}` }}>B</span>
                )}
              </div>

              {/* Name + Bot badge + Email + Gender */}
              <div className="flex items-center gap-1.5">
                <h2 className="text-lg font-bold" style={{ color: colors.text }}>{profile.nickname}</h2>
                {profile.isBot && config.showBotBadge !== false && (
                  <span className="rounded px-1.5 py-0.5 text-[9px] font-bold text-white" style={{ backgroundColor: '#9333ea' }}>BOT</span>
                )}
              </div>
              {profile.email && (
                <span className="mt-0.5 text-[10px]" style={{ color: colors.textMuted }}>
                  {uid === me?.uid ? profile.email : maskEmail(profile.email, me?.isAdmin)}
                </span>
              )}
              {genderLabel && (
                <span className="mt-0.5 text-xs" style={{ color: genderColor }}>{genderLabel}</span>
              )}
              {profile.ageGroup && (
                <span className="mt-0.5 text-[11px]" style={{ color: colors.textMuted }}>{profile.ageGroup}</span>
              )}

              {/* Introduction (supports both 'introduction' and 'selfIntroduction' field names) */}
              {(profile.introduction || (profile as any).selfIntroduction) && (
                <p className="mt-3 rounded-lg px-3 py-2 text-center text-sm"
                  style={{ backgroundColor: colors.card, color: colors.textSecondary, maxWidth: '100%' }}>
                  {profile.introduction || (profile as any).selfIntroduction}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-1.5 px-4 pb-4">
              {/* Copy frequency */}
              {profile.currentLocation && !isBlocked && (
                <button
                  onClick={handleCopyFreq}
                  className="flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-colors"
                  style={{ backgroundColor: colors.card, color: copiedFreq ? colors.primary : colors.text }}
                >
                  <IconCopy size={14} color={copiedFreq ? colors.primary : colors.textMuted} />
                  {copiedFreq ? t('common.done') : t('profile.copyFrequency')}
                </button>
              )}

              {isBlocked ? (
                /* Blocked user: only show unblock button */
                <button
                  onClick={handleBlock}
                  className="flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold"
                  style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}
                >
                  ✅ {t('profile.unblock')}
                </button>
              ) : me && uid !== me.uid && (
                <>
                  {/* Send DM */}
                  <button
                    onClick={() => onSendDM?.(uid, profile.nickname)}
                    className="flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-white"
                    style={{ backgroundColor: colors.primary }}
                  >
                    ✉️ {t('profile.sendDM')}
                  </button>

                  {/* Report + Block row */}
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => onReport?.(uid, profile.nickname)}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold"
                      style={{ backgroundColor: `${colors.warning}15`, color: colors.warning }}
                    >
                      🚩 {t('profile.report')}
                    </button>
                    <button
                      onClick={handleBlock}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold"
                      style={{ backgroundColor: `${colors.error}15`, color: colors.error }}
                    >
                      🚫 {t('profile.block')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm" style={{ color: colors.textMuted }}>
              {i18n.language === 'ko' ? '탈퇴했거나 존재하지 않는 사용자입니다.' : 'This user has been deleted or does not exist.'}
            </p>
          </div>
        )}
      </div>

      {/* Full image viewer with pinch zoom */}
      {showFullImage && profile?.profileImageUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90"
          data-allow-pinch
          style={{ touchAction: 'pinch-zoom pan-x pan-y' }}
          onClick={() => setShowFullImage(false)}>
          <img src={profile.profileImageUrl} alt=""
            className="max-h-[80vh] max-w-[90vw] rounded-lg object-contain"
            data-allow-pinch
            style={{ touchAction: 'pinch-zoom manipulation' }}
            onClick={(e) => e.stopPropagation()} />
          <button onClick={(e) => { e.stopPropagation(); setShowFullImage(false); }}
            className="absolute right-4 flex h-10 w-10 items-center justify-center rounded-full text-xl font-bold text-white"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)', top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
